import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

const FAQS = [
  {
    q: 'Is my community\'s data isolated from other societies on the platform?',
    a: 'Yes, strictly. GateKeeper Pro is multi-tenant by design — every notice, vehicle, visitor and resident is scoped to a community_id. Residents, management and guards from one society can never see data from another.',
  },
  {
    q: 'Are there any ads in the app? Will my residents see promotions?',
    a: 'No. GateKeeper Pro shows zero advertisements — no banner ads, no sponsored notices, no third-party trackers feeding promotions. Your residents see only what your management chooses to share. We make money from society subscriptions, not from selling attention.',
  },
  {
    q: 'Can a resident use the app on multiple devices? Is it tied to one phone?',
    a: 'No device lock-in. Every account is keyed by email + OTP, so residents can log in from their phone, tablet, work laptop and family iPad — all simultaneously. Lose your phone? Just log in from any other device with your email. Your account isn\'t bound to a SIM, IMEI, or app-store ID.',
  },
  {
    q: 'How do residents install the app? Is it on the App Store or Play Store?',
    a: 'It\'s a Progressive Web App (PWA) — no store listing required. Residents open gatekeeper.homes in Safari (iOS) or Chrome (Android) and tap "Install" / "Add to Home Screen". The app runs full-screen, supports push notifications, and updates automatically — no install delays.',
  },
  {
    q: 'How does ANPR (Automatic Number Plate Recognition) work?',
    a: 'We integrate with your existing gate camera via the Cloud. As plates are read, GateKeeper Pro polls every few seconds, deduplicates, and either flags it GREEN (approved resident vehicle), RED (blacklisted) or NO_PLATE. Residents get a real-time push when their car arrives. Approved vehicles also sync outbound to a Cloud Allowlist for the camera to act on.',
  },
  {
    q: 'How does login work? Do residents need passwords?',
    a: 'No passwords. Residents enter their email, receive a one-time code, and they\'re in. We use httpOnly auth cookies plus an Authorization header for added safety. Optional "Keep me logged in" extends the session.',
  },
  {
    q: 'Can I run polls, send notices, and book facilities?',
    a: 'Yes. Notices have read-receipts and priority pinning. Surveys have explicit expiry, post-vote stats, and a Trending Now card on every dashboard. Facilities support time-slot bookings with conflict prevention. All of it is push-notified.',
  },
  {
    q: 'How is the chat data stored?',
    a: 'Resident neighbour chats are intentionally transient — messages auto-purge after 24 hours, by design. This keeps coordination fast and prevents your community group from becoming a forever-archive of forgotten messages.',
  },
  {
    q: 'How do payments work?',
    a: 'Maintenance invoices and UPI payments are on our roadmap (Razorpay/Stripe integration). For now, the app handles every other workflow — gate, residents, staff, vehicles, surveys, notices, recommendations and bookings. Reach out and we\'ll prioritize what your society needs.',
  },
];

export const FAQ = () => (
  <section
    id="faq"
    className="py-24 lg:py-28 bg-[#F8FAFC]"
    data-testid="faq-section"
  >
    <div className="max-w-3xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-12">
        <p
          className="text-base md:text-lg tracking-wide uppercase text-slate-500 font-semibold mb-3"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Frequently asked
        </p>
        <h3
          className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold leading-tight text-slate-900"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="faq-headline"
        >
          The fine print, up front.
        </h3>
      </div>
      <Accordion type="single" collapsible className="space-y-3">
        {FAQS.map((f, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="bg-white rounded-xl border border-slate-200 px-5 data-[state=open]:shadow-md transition-shadow"
            data-testid={`faq-item-${i}`}
          >
            <AccordionTrigger
              className="text-left text-base font-semibold text-slate-900 hover:no-underline py-5"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {f.q}
            </AccordionTrigger>
            <AccordionContent
              className="text-sm text-slate-600 leading-relaxed pb-5"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);
